import React from 'react';
import { Box, Typography } from '@mui/material';
import { LineItem, LineItemLocalState } from '../../../services/fulfillment/types';
import { groupBinsByZone } from '../../../services/fulfillment/utils';
import {
  filterBinsForStatus,
  getEffectiveStatus,
  getExceptionLabel,
} from '../FulfillmentOrderDetail.utils';

interface Props {
  item: LineItem;
  ls: LineItemLocalState | undefined;
  qcZoneTypes: string[];
}

export const LocationCell: React.FC<Props> = ({ item, ls, qcZoneTypes }) => {
  if (!ls) return <Typography sx={{ fontSize: 13, color: 'grey.500' }}>—</Typography>;

  const effectiveStatus = getEffectiveStatus(ls);

  if (effectiveStatus === 'picked' || effectiveStatus === 'packed') {
    const binCode = ls.pendingBin?.code ?? ls.fulfillmentBinCode;
    return (
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'grey.800' }}>
        {binCode ?? '—'}
      </Typography>
    );
  }

  if (effectiveStatus === 'exception') {
    const excType = ls.pendingStatus ? ls.pendingExceptionType : ls.exceptionType;
    const notes = ls.pendingStatus ? ls.pendingNotes : ls.notes;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'error.main' }}>
          {getExceptionLabel(excType)}
        </Typography>
        {notes && <Typography sx={{ fontSize: 12, color: 'grey.600' }}>{notes}</Typography>}
      </Box>
    );
  }

  const zoneGroups = groupBinsByZone(
    filterBinsForStatus(item.binLocations, effectiveStatus, qcZoneTypes),
  );
  if (zoneGroups.length === 0) {
    const noValidLocation =
      effectiveStatus === 'quality-check'
        ? 'Not in a valid QC location'
        : 'Not in Pickable location';
    return <Typography sx={{ fontSize: 13, color: 'grey.500' }}>{noValidLocation}</Typography>;
  }
  return (
    <>
      {zoneGroups.map(group => (
        <Box key={group.zoneLabel} sx={{ mb: group.bins.length > 0 ? 1 : 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'grey.600', mb: 0.25 }}>
            {group.zoneLabel}
          </Typography>
          {group.bins.map(bin => (
            <Box
              key={bin.binCode}
              sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', pl: 1 }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'grey.800' }}>
                {bin.binCode}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'grey.500' }}>qty {bin.quantity}</Typography>
            </Box>
          ))}
        </Box>
      ))}
    </>
  );
};
